import { useCallback, useState } from 'react';
import { Flex } from '@chakra-ui/react';
import { Banner, Modal } from '@morressier/alembic';

import { FormProvider, useForm } from 'react-hook-form';

import { useUnassignedMember } from 'src/hooks/useUnassignedMember';
import AssignMembersHeader from './AssignMembersHeader';
import MembersTable from './MembersTable';
import PaginationSection from './PaginationSection';
import FooterActions from './FooterActions';
import { IJournalMember } from '@publishing-engine/domain/models';
import SelectedAssignedMemberModal from './SelectedAssignedMemberModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;

  submissionContextId: string;
  roles: string[];
  journalName: string;
}

export type FormValues = {
  assignments: Record<string, IJournalMember>;
  searchParams: string;
};

const AssignMembers = ({
  workspaceId,
  submissionContextId,
  roles,
  isOpen,
  onClose,
  journalName
}: Props) => {
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    currentPageData,
    total,
    totalPages,
    startIndex,
    endIndex,
    isLoading,
    isFetching
  } = useUnassignedMember(workspaceId, searchValue, page);

  const methods = useForm<FormValues>({
    defaultValues: {
      assignments: {}
    }
  });

  const {
    watch,
    setValue,
    formState: { errors },
    clearErrors
  } = methods;

  const searchParams = watch('searchParams');

  const handleSearch = useCallback(() => {
    setSearchValue(searchParams.trim());
    setPage(1);
  }, [searchParams]);

  const hasRoleErrors = errors.assignments;

  const handleOpenModal = () => setIsOpenModal(true);
  const handleCloseModal = () => {
    setIsOpenModal(false);
    clearErrors();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width={{ base: 'calc(100vw - 16px)', md: '960px' } as any}
    >
      <FormProvider {...methods}>
        <Flex direction="column" gap={6} p={{ base: 4, md: 8 }}>
          <AssignMembersHeader
            total={total}
            startIndex={startIndex}
            endIndex={endIndex}
            searchParams={searchParams}
            setValue={setValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
            isLoading={isLoading || isFetching}
          />

          {hasRoleErrors && (
            <Banner
              variant="error"
              heading="Assign all roles before saving changes"
            />
          )}

          <MembersTable
            roles={roles}
            members={currentPageData}
            submissionContextId={submissionContextId}
            isLoading={isLoading || isFetching}
          />

          {totalPages > 1 && !isLoading && !isFetching && (
            <PaginationSection
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          )}

          <FooterActions
            onSave={handleOpenModal}
            onClose={onClose}
            isLoading={isLoading || isFetching}
          />
        </Flex>
        <SelectedAssignedMemberModal
          journalName={journalName}
          onClose={handleCloseModal}
          roles={roles}
          submissionContextId={submissionContextId}
          workspaceId={workspaceId}
          onParentModalClose={onClose}
          isOpen={isOpenModal}
        />
      </FormProvider>
    </Modal>
  );
};

export default AssignMembers;
